import React, { useEffect, useState } from "react";
import { logisticsApi } from "../../api/auctiongate.js";

/**
 * LeadModal — popup that proposes "exact quote with manager callback".
 * Shown after the user makes a valid From/To selection in any of the 3 tabs.
 *
 * Backend: POST /api/logistics/leads (extensionapi). Same conceptual flow as
 * the BidCars Europe-map modal (POST /api/logistics/leads on prod).
 *
 * Props:
 *   open, onClose, scenario, summary (object: { from, to, route, estimate })
 *   payload (object: vehicleId, lotNumber, vin, origin, originState,
 *            port, euPort, euCountry, fromMode, estimateUsd)
 */
export default function LeadModal({ open, onClose, scenario, summary, payload }) {
  const [step, setStep] = useState(1); // 1 = pitch, 2 = form, 3 = success
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setName("");
    setPhone("");
    setEmail("");
    setErr("");
    setSubmitting(false);
  }, [open]);

  if (!open) return null;

  async function submit() {
    setErr("");
    if (!name.trim() || !phone.trim()) {
      setErr("Please enter your name and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await logisticsApi.createLead({
        scenario,
        ...payload,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        notes: summary?.route ? `Route: ${summary.route}` : "",
      });
      setStep(3);
    } catch (e) {
      setErr(e?.message || "Failed to submit. Try again later.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="lead-modal__overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="lead-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {step === 1 && (
          <>
            <div className="lead-modal__icon">📞</div>
            <h2 className="lead-modal__title">Get an exact quote with a manager</h2>
            <p className="lead-modal__sub">
              We&apos;ve calculated an approximate price. Our manager will call you back to confirm
              the exact cost and current rates.
            </p>

            <div className="lead-modal__summary">
              <div className="lead-modal__summary-row">
                <span className="lead-modal__summary-label">From</span>
                <span className="lead-modal__summary-value">{summary?.from || "—"}</span>
              </div>
              <div className="lead-modal__summary-row">
                <span className="lead-modal__summary-label">To</span>
                <span className="lead-modal__summary-value">{summary?.to || "—"}</span>
              </div>
              {summary?.estimate && (
                <div className="lead-modal__summary-row lead-modal__summary-row--total">
                  <span className="lead-modal__summary-label">Approximate</span>
                  <span className="lead-modal__summary-value">{summary.estimate}</span>
                </div>
              )}
            </div>

            <div className="lead-modal__actions">
              <button type="button" className="lead-modal__btn lead-modal__btn--ghost" onClick={onClose}>
                Not now
              </button>
              <button type="button" className="lead-modal__btn lead-modal__btn--primary" onClick={() => setStep(2)}>
                Yes, calculate exact price
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="lead-modal__icon">📋</div>
            <h2 className="lead-modal__title">Manager will call you back</h2>
            <p className="lead-modal__sub">
              Leave your contact details. Our logistics manager will reach out shortly with a confirmed quote.
            </p>

            <label className="lead-modal__field">
              <span className="lead-modal__field-label">Your name *</span>
              <input
                className="lead-modal__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
              />
            </label>
            <label className="lead-modal__field">
              <span className="lead-modal__field-label">Phone *</span>
              <input
                className="lead-modal__input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
              />
            </label>
            <label className="lead-modal__field">
              <span className="lead-modal__field-label">Email (optional)</span>
              <input
                className="lead-modal__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>

            {err && <div className="lead-modal__err">{err}</div>}

            <div className="lead-modal__actions">
              <button type="button" className="lead-modal__btn lead-modal__btn--ghost" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                type="button"
                className="lead-modal__btn lead-modal__btn--primary"
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Send request"}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="lead-modal__icon lead-modal__icon--success">✅</div>
            <h2 className="lead-modal__title">Request received</h2>
            <p className="lead-modal__sub">
              Our manager will get in touch with you shortly to confirm the exact delivery cost.
            </p>
            <div className="lead-modal__actions">
              <button type="button" className="lead-modal__btn lead-modal__btn--primary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
